import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { LabeledInfo } from 'ui-components/components/labeled-info';

import { FormattedWeight } from '../../../../components/formatted-weight';
import { useEnableV3CustomsWeightField } from '../../../../feature-flags/hooks/use-enable-v3-customs-weight-field';
import { useEnableCpscCertificateFields } from '../../../../feature-flags/hooks/use-enable-cpsc-certificate-fields';
import Global from '../../../../store/global-selectors';
import { useInternationalRequirements } from '../../../../hooks/use-international-requirements';
import type { WeightValue } from '../../../../prop-types/weight-types';
import { WeightUnit } from '../../../../enums/weight-unit';
import { Unknown } from '../../../../constants/currency-codes';
import { messages as countryNameMessages } from '../../../country-names.messages';
import { FormattedCurrency } from '../../../formatted-currency';
import type { CustomsItem } from '../../types';

import { messages } from '../messages';
import style from './customs-declaration.module.css';

type ReadOnlyDeclarationProps = {
  customItem: CustomsItem;
};

const defaultWeightValue: WeightValue = { value: 0, unit: WeightUnit.Unknown };
const defaultUnitPrice = { value: 0, code: Unknown };

export const ReadOnlyDeclaration = ({ customItem }: ReadOnlyDeclarationProps) => {
  const {
    description = '',
    weight = defaultWeightValue,
    sku = '',
    mid = '',
    certificateVersionId = '',
    certifierId = '',
    quantity = 0,
    unitPrice = defaultUnitPrice,
    harmonizedTariffCode = '',
    originCountryCode = '',
  } = customItem;
  const intl = useIntl();
  const enableV3CustomsWeightField = useEnableV3CustomsWeightField();
  const isCpscEnabled = useEnableCpscCertificateFields();
  const { applyForTariffs } = useInternationalRequirements();
  const countries = useSelector(Global.selectReferenceDatagetCountries);
  const matchingCountry = countries.find((country: { code: string }) => country.code === originCountryCode);
  const countryCode = matchingCountry?.code as keyof typeof countryNameMessages;
  const countryMessage = countryCode ? countryNameMessages[countryCode] : messages.unknownCountry;
  const countryName = intl.formatMessage(countryMessage);

  return (
    <div className={style['read-only']}>
      <div className={style.row}>
        <LabeledInfo className={style['half-row-item']} label={intl.formatMessage(messages.descriptionLabel)} showMdash={!description}>
          <span>{description}</span>
        </LabeledInfo>
        <LabeledInfo
          className={style['half-row-item']}
          label={intl.formatMessage(messages.itemValueLabel)}
          showMdash={!unitPrice || !unitPrice.value}
        >
          <FormattedCurrency currencyCode={unitPrice?.code} value={unitPrice?.value} />
        </LabeledInfo>
      </div>
      <div className={style.row}>
        <LabeledInfo
          className={style['half-row-item']}
          label={intl.formatMessage(messages.countryOfOriginLabel)}
          showMdash={!originCountryCode}
        >
          <span>{countryName}</span>
        </LabeledInfo>
        <LabeledInfo className={style['half-row-item']} label={intl.formatMessage(messages.quantityLabel)} showMdash={!quantity}>
          <span>{quantity}</span>
        </LabeledInfo>
      </div>
      <div className={style.row}>
        <LabeledInfo
          className={style['half-row-item']}
          label={intl.formatMessage(messages.harmonizationLabel)}
          showMdash={!harmonizedTariffCode}
        >
          <span>{harmonizedTariffCode}</span>
        </LabeledInfo>
        <LabeledInfo
          className={style['half-row-item']}
          label={intl.formatMessage(messages.totalValueLabel)}
          showMdash={!unitPrice || !unitPrice.value || !quantity}
        >
          <FormattedCurrency currencyCode={unitPrice?.code} value={unitPrice?.value * quantity} />
        </LabeledInfo>
      </div>
      <div className={style.row}>
        <LabeledInfo className={style['half-row-item']} label={intl.formatMessage(messages.skuLabel)} showMdash={!sku}>
          <span>{sku}</span>
        </LabeledInfo>
        {enableV3CustomsWeightField && (
          <LabeledInfo
            className={style['half-row-item']}
            label={intl.formatMessage(messages.weightLabel)}
            showMdash={!weight || !weight.value}
          >
            <FormattedWeight weight={weight} />
          </LabeledInfo>
        )}
      </div>
      {applyForTariffs && (
        <div className={style.row}>
          <LabeledInfo className={style['half-row-item']} label={intl.formatMessage(messages.midLabel)} showMdash={!mid}>
            <span>{mid}</span>
          </LabeledInfo>
        </div>
      )}
      {isCpscEnabled && applyForTariffs && (
        <div className={style.row}>
          <LabeledInfo
            className={style['half-row-item']}
            label={intl.formatMessage(messages.certificateVersionIdLabel)}
            showMdash={!certificateVersionId}
          >
            <span>{certificateVersionId}</span>
          </LabeledInfo>
          <LabeledInfo className={style['half-row-item']} label={intl.formatMessage(messages.certifierIdLabel)} showMdash={!certifierId}>
            <span>{certifierId}</span>
          </LabeledInfo>
        </div>
      )}
    </div>
  );
};
